import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { MarkdownRenderer as LegacyMarkdownRenderer } from '@edusense/shared-ui-content';
import { parseContentDocument } from './document.mjs';
import { renderContentDocument } from './renderer.mjs';
import { mountContentStudio } from './studio.mjs';
import { hydrateContentDocument } from './reader.mjs';
import './content-studio.css';
import './studio.css';
import './workspace.css';
function NativeContent({ content, testId }) {
    const root = useRef(null);
    useEffect(()=>{
        if (!root.current) return;
        const reader = hydrateContentDocument(root.current, content);
        return ()=>reader.dispose();
    }, [
        content
    ]);
    return /*#__PURE__*/ _jsx("div", {
        ref: root,
        "data-testid": testId,
        dangerouslySetInnerHTML: {
            __html: renderContentDocument(content)
        }
    });
}
/** The editor, preview, and learner all consume the same serialized document. */ export function ContentMarkdownRenderer(props) {
    try {
        const document1 = parseContentDocument(props.markdown ?? '');
        if (document1) return /*#__PURE__*/ _jsx(NativeContent, {
            content: document1,
            testId: props.testId ?? 'content-document-preview'
        });
    } catch (error) {
        return /*#__PURE__*/ _jsxs("p", {
            role: "alert",
            children: [
                "콘텐츠 형식을 확인해 주세요: ",
                error instanceof Error ? error.message : '알 수 없는 문서'
            ]
        });
    }
    return /*#__PURE__*/ _jsx(LegacyMarkdownRenderer, {
        ...props
    });
}
export const ContentStudio = /*#__PURE__*/ forwardRef(function ContentStudio({ markdown, onChange }, ref) {
    const root = useRef(null);
    const callback = useRef(onChange);
    const session = useRef(null);
    const currentMarkdown = useRef(null);
    callback.current = onChange;
    useImperativeHandle(ref, ()=>({
            async flush () {
                if (!session.current) throw new Error('편집기를 준비한 뒤 다시 시도해 주세요.');
                await session.current.flush();
            }
        }), []);
    // Keep cursor and local draft identity on editor echoes; reopen external lecture changes.
    useEffect(()=>{
        if (!root.current || currentMarkdown.current === markdown) return;
        session.current?.destroy();
        session.current = null;
        currentMarkdown.current = markdown;
        try {
            session.current = mountContentStudio(root.current, {
                initialMarkdown: markdown,
                onChange: (value)=>{
                    currentMarkdown.current = value;
                    callback.current(value);
                }
            });
        } catch (error) {
            const message = document.createElement('p');
            message.setAttribute('role', 'alert');
            message.textContent = `원문 형식을 확인한 뒤 다시 열어 주세요. ${error instanceof Error ? error.message : ''}`;
            root.current.replaceChildren(message);
        }
    }, [
        markdown
    ]);
    useEffect(()=>()=>{
            session.current?.destroy();
            session.current = null;
            currentMarkdown.current = null;
        }, []);
    return /*#__PURE__*/ _jsx("div", {
        ref: root
    });
});
